import { useState, createContext, ReactNode, useContext } from 'react'
import { TimelineEvent } from '../../Types'
import { LazyComponentWrapper } from 'components/LazyComponentWrapper/LazyComponentWrapper'
import { RestoreVersionModal } from 'components/RestoreVersionModal/RestoreVersionModal'
import ActionModal from 'components/NavRightClickMenu/components/ActionModal/ActionModal'
import { AncestorProject } from 'types'
import { message } from 'antd'
import { FolderClient } from 'api/clients/FolderClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { getArticlePath } from 'utils/urlUtils'
import { useHistory, generatePath, useRouteMatch } from 'react-router-dom'
import { reportError } from 'utils/error'
import { useAuth } from 'hooks/UseAuth'
import { DOC_ACTIVITY_BRANCH_PATH } from 'paths'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

export enum ActivityActionModalType {
  Branch = 'branch',
  Restore = 'restore',
  Copy = 'copy',
}

export interface ActivityActionModalContextValue {
  openModal: (modal: ActivityActionModalType, event: TimelineEvent) => void
  closeModal: () => void
}

const ActivityActionModalContext = createContext<ActivityActionModalContextValue>({
  openModal: () => {},
  closeModal: () => {},
})

const ActivityActionModalContextProvider = ActivityActionModalContext.Provider

interface ActivityActionModalProviderProps {
  children: ReactNode
}

export const ActivityActionModalProvider = ({ children }: ActivityActionModalProviderProps) => {
  const auth = useAuth()
  const [event, setEvent] = useState<TimelineEvent>()
  const [modal, setModal] = useState<ActivityActionModalType>()
  const history = useHistory()
  const onDocActivityBranchPath = useRouteMatch(DOC_ACTIVITY_BRANCH_PATH)

  const openModal = (modal: ActivityActionModalType, event: TimelineEvent) => {
    setEvent(event)
    setModal(modal)
  }

  const closeModal = () => {
    setEvent(undefined)
    setModal(undefined)
  }

  const value = {
    openModal,
    closeModal,
  }

  const handleVisibility = (visible: boolean) => {
    if (visible) {
      return
    }
    closeModal()
  }

  const copy = async ({
    selectedFolder,
    newWorkspaceId,
  }: {
    selectedFolder: AncestorProject | null
    newWorkspaceId: number
  }) => {
    if (!event) {
      return
    }
    const selectedFolderId = !!selectedFolder ? selectedFolder.id : null
    const hideLoading = message.loading('Copying 1 item...', 0)
    const response = await FolderClient.bulkCopyFiles({
      folderIds: [],
      articleIds: [event.article.key],
      fileIds: [],
      destinationFolderId: selectedFolderId,
      destinationWorkspaceId: newWorkspaceId,
      articleVersions: { [event.article.key]: event.version },
    })

    if (response.type === SUCCESS_RESPONSE_TYPE) {
      hideLoading()
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      history.push(getArticlePath({ slug: response.data.files[0].slug! }))
    } else {
      hideLoading()
      showToastMessage('An error occurred! Our team has been notified and we are looking into it.', 'error')
      reportError('Failed to copy document from an event in the activity screen', {
        severity: 'error',
        metaData: { event },
        user: { id: auth?.id },
      })
    }
  }

  const renderModal = () => {
    if (!event || !modal) {
      return null
    }
    if (modal === ActivityActionModalType.Branch) {
      if (onDocActivityBranchPath) return

      history.replace(
        generatePath(DOC_ACTIVITY_BRANCH_PATH, {
          slug: event.article.slug,
          parent_version: event.version,
          eventId: event.id,
        }),
      )
      setEvent(undefined)
      setModal(undefined)

      return
    }
    if (modal === ActivityActionModalType.Restore) {
      return (
        <LazyComponentWrapper
          component={<RestoreVersionModal visible setVisible={handleVisibility} event={event} onClose={closeModal} />}
        />
      )
    }
    if (modal === ActivityActionModalType.Copy) {
      return (
        <LazyComponentWrapper
          component={
            <ActionModal
              visible
              setVisible={handleVisibility}
              selectedFiles={[
                {
                  type: 'article',
                  title: '',
                  id: event.article.key,
                  key: event.article.key,
                },
              ]}
              submit={copy}
              ctaText="Copy"
              isMove={false}
            />
          }
        />
      )
    }
  }

  return (
    <ActivityActionModalContextProvider value={value}>
      {children}
      {renderModal()}
    </ActivityActionModalContextProvider>
  )
}

export const useActivityActionModalContext = () => {
  return useContext(ActivityActionModalContext)
}
